/**
 * Everything the suite has told this person, in one place, with a way to act on each of it.
 *
 * WHY IT IS NOT THE BELL. The bell fetches with `Application: "QMS"` and clicks through with
 * `navigate()` -- both correct, because everything it can show is a route in this app. An inbox
 * that shows a Parkour Docs mention cannot navigate to it: that screen is on another origin, and
 * `navigate("/document/...")` would land on this app's not-found page. So the inbox fetches with
 * no application filter and leaves by handoff instead.
 *
 * THE HANDOFF CARRIES THE PERSONA, AND THAT IS THE PART THAT MAKES THE LINK WORK. A notification
 * addressed to Nephrology Team Member concerns a record only that position can see. Arriving under
 * whatever persona the reader last used would show them a denial, which reads as a broken link
 * rather than as a scoping rule. `handoffVisibility` builds the three-part hint and
 * `buildSsoHandoffUrl` attaches it.
 *
 * AND THE EMAIL POINTS HERE RATHER THAN THERE. The handoff URL carries an access token, which is
 * right for a click inside a live session and wrong in a mailbox -- it would be a credential in
 * every forward of that message. So the email links to this page with no token, the person signs
 * in as usual, and the deep link is minted here from a session that already exists. See
 * cres/utils/notification_template.py.
 */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Box, Button, Chip, CircularProgress, Divider, MenuItem, Select, Tooltip, Typography,
} from "@mui/material";
import {LaunchOutlined, MarkEmailReadOutlined, NotificationsNoneOutlined} from "@mui/icons-material";

import PageHeader from "../components/Common/PageHeader";
import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";
import {useSessionContext} from "../util/SessionContext";
import {buildSsoHandoffUrl, handoffVisibility} from "../util/ssoAuthenticate";
import {applicationOf} from "../util/suiteApplications";
import {
    AppNotification, EMAIL_MODES, EmailMode, fetchEmailPreference, fetchNotifications,
    markAllNotificationsRead, markNotificationRead, setEmailPreference,
} from "../data/notifications";
import {Blue3, Grey2, Grey5, Grey6, Grey17, White} from "../theme/styles";

function when(iso: string): string {
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return "";
    const mins = Math.floor((Date.now() - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} minutes ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} days ago`;
    return new Date(t).toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

export default function Inbox() {
    const sessionContext = useSessionContext();
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [items, setItems] = useState<AppNotification[]>([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    // THE SETTING LIVES WHERE IT IS FELT. Somebody decides they get too much email while looking
    // at the list of it, not while hunting through an account page -- and a preference nobody can
    // find is one nobody uses, which leaves turning email on a decision taken for them.
    const [mode, setMode] = useState<EmailMode>("IMMEDIATE");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // "" IS EVERY APPLICATION. The default argument is this app's own name, which is what
            // the bell wants and the opposite of what an inbox wants.
            const result = await fetchNotifications(api, "");
            setItems(result.notifications);
            setUnread(result.unreadCount);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetchEmailPreference(api).then(setMode).catch(() => { /* the default stands */ });
    }, [api]);

    const changeMode = async (next: EmailMode) => {
        const previous = mode;
        setMode(next);                       // answer the click immediately
        try {
            setMode(await setEmailPreference(api, next));
        } catch {
            setMode(previous);               // and put it back if the server disagreed
        }
    };

    const open = async (one: AppNotification) => {
        if (!one.read) {
            setItems(prev => prev.map(x => (x.usid === one.usid ? {...x, read: true} : x)));
            setUnread(u => Math.max(0, u - 1));
            try { await markNotificationRead(api, one.usid); } catch { /* best effort */ }
        }

        const application = applicationOf(one.applications);
        if (!application || !one.url) return;

        const destination = `${application.url}${one.url.startsWith("/") ? "" : "/"}${one.url}`;
        // MINTED AT CLICK TIME, from this session, under this persona -- never stored and never
        // sent anywhere.
        window.open(
            buildSsoHandoffUrl(destination, sessionContext?.getAccessToken?.() ?? null,
                               handoffVisibility(sessionContext)),
            "_blank", "noopener");
    };

    const markAll = async () => {
        setItems(prev => prev.map(one => ({...one, read: true})));
        setUnread(0);
        try { await markAllNotificationsRead(api); } catch { /* best effort */ }
    };

    return (
        <>
        <PageHeader title="Inbox"/>
        <Box sx={{padding: "24px 30px", display: "flex", flexDirection: "column", gap: "16px"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                <Typography sx={{fontSize: 14, color: Grey6}}>
                    Everything the suite has told you, newest first.
                    {unread > 0 ? ` ${unread} unread.` : ""}
                </Typography>
                <Box sx={{flexGrow: 1}}/>
                <Tooltip title="How much of this is also emailed to you">
                    <Select size="small" value={mode} variant="outlined"
                            onChange={event => changeMode(event.target.value as EmailMode)}
                            sx={{fontSize: 13, minWidth: 190,
                                 "& .MuiSelect-select": {paddingTop: "5px", paddingBottom: "5px"}}}
                            renderValue={value => {
                                const chosen = EMAIL_MODES.find(one => one.value === value);
                                return `Email: ${chosen ? chosen.label.toLowerCase() : value}`;
                            }}>
                        {EMAIL_MODES.map(one => (
                            <MenuItem key={one.value} value={one.value}
                                      sx={{display: "block", paddingTop: "6px"}}>
                                <Typography sx={{fontSize: 13.5, color: Grey17}}>{one.label}</Typography>
                                <Typography sx={{fontSize: 11.5, color: Grey6}}>{one.detail}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </Tooltip>
                {unread > 0 ? (
                    <Button size="small" startIcon={<MarkEmailReadOutlined/>} onClick={markAll}>
                        Mark all read
                    </Button>
                ) : null}
            </Box>

            {loading ? <CircularProgress size={22}/> : null}

            {!loading && items.length === 0 ? (
                <Box sx={{display: "flex", flexDirection: "column", alignItems: "center",
                          gap: 1, padding: "64px 0", color: Grey5}}>
                    <NotificationsNoneOutlined sx={{fontSize: 40}}/>
                    <Typography sx={{fontSize: 14}}>Nothing yet.</Typography>
                </Box>
            ) : null}

            <Box sx={{display: "flex", flexDirection: "column", border: `1px solid ${Grey2}`,
                      borderRadius: "8px", backgroundColor: White, overflow: "hidden"}}>
                {items.map((one, index) => {
                    const application = applicationOf(one.applications);
                    // A ROW WITH NOWHERE TO GO IS STILL A ROW. `data.url` is a convention rather
                    // than a contract -- a notification type that omits it is readable here and
                    // simply does not lead anywhere, which is better than a link that 404s.
                    const reachable = !!application && !!one.url;
                    return (
                        <React.Fragment key={one.usid}>
                            {index > 0 ? <Divider/> : null}
                            <Box onClick={() => open(one)}
                                 sx={{display: "flex", gap: 2, alignItems: "flex-start",
                                      padding: "14px 18px",
                                      cursor: reachable ? "pointer" : "default",
                                      backgroundColor: one.read ? White : "rgba(25,151,214,0.045)",
                                      "&:hover": reachable ? {backgroundColor: "rgba(0,0,0,0.025)"}
                                                           : undefined}}>
                                {/* UNREAD IS A DOT, NOT A BOLD ROW. A list where half the lines are
                                    bold is harder to scan than one with a mark in the margin. */}
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", marginTop: "6px",
                                          flexShrink: 0,
                                          backgroundColor: one.read ? "transparent" : Blue3}}/>
                                <Box sx={{flexGrow: 1, minWidth: 0}}>
                                    <Typography sx={{fontSize: 14, fontWeight: one.read ? 500 : 600,
                                                     color: Grey17}}>
                                        {one.title}
                                    </Typography>
                                    {one.body ? (
                                        <Typography sx={{fontSize: 13, color: Grey6, marginTop: "2px"}}>
                                            {one.body}
                                        </Typography>
                                    ) : null}
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1,
                                              marginTop: "6px"}}>
                                        <Chip size="small" label={application?.name || "All apps"}
                                              sx={{fontSize: 11, height: 20,
                                                   backgroundColor: Grey2, color: Grey6}}/>
                                        <Typography sx={{fontSize: 12, color: Grey5}}>
                                            {when(one.createdDatetime)}
                                        </Typography>
                                    </Box>
                                </Box>
                                {reachable ? (
                                    <LaunchOutlined sx={{fontSize: 17, color: Grey5, marginTop: "2px"}}/>
                                ) : null}
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
        </>
    );
}
